import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { OrderColumn } from '@/components/orders/OrderColumn';
import { SearchField } from '@/components/orders/SearchField';
import { ShopPauseControl } from '@/components/shop/ShopPauseControl';
import { OrderDetailDrawer } from '@/features/orders/OrderDetailDrawer';
import { OnboardingTour } from '@/components/onboarding/OnboardingTour';
import { useOrders } from '@/hooks/useOrders';
import { useTitleBadge } from '@/hooks/useTitleBadge';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useWakeLock } from '@/hooks/useWakeLock';
import { useAcceptOrder, useCompleteOrder } from '@/hooks/useOrderActions';
import { todayISO } from '@/lib/time';
import { matchesOrderQuery } from '@/lib/search';
import type { OrderDTO, OrderStatus } from '@/types/api';

const NEW_HIGHLIGHT_MS = 12_000;
const TERMINAL_CAP = 8;

// Status buckets — pending_payment is intentionally excluded (cashier can't act on it).
const NEW_STATUSES: OrderStatus[] = ['paid', 'pending'];
const PREPARING_STATUSES: OrderStatus[] = ['preparing'];
const COMPLETED_STATUSES: OrderStatus[] = ['completed'];
const OTHER_STATUSES: OrderStatus[] = ['cancelled', 'error'];

interface Buckets {
  newOrders: OrderDTO[];
  preparing: OrderDTO[];
  completed: OrderDTO[];
  other: OrderDTO[];
}

function bucketize(items: OrderDTO[]): Buckets {
  const newOrders: OrderDTO[] = [];
  const preparing: OrderDTO[] = [];
  const completed: OrderDTO[] = [];
  const other: OrderDTO[] = [];
  for (const o of items) {
    if (NEW_STATUSES.includes(o.status)) newOrders.push(o);
    else if (PREPARING_STATUSES.includes(o.status)) preparing.push(o);
    else if (COMPLETED_STATUSES.includes(o.status)) completed.push(o);
    else if (OTHER_STATUSES.includes(o.status)) other.push(o);
  }
  return { newOrders, preparing, completed, other };
}

export function OrdersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const initialOpen = paramId ? Number(paramId) : null;

  const [openId, setOpenId] = useState<number | null>(initialOpen);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setOpenId(initialOpen);
  }, [initialOpen]);

  // Single source of truth: today's orders, bucketed client-side.
  const today = todayISO();
  const { data, isLoading, refetch } = useOrders({ date: today, limit: 200 });
  const items = useMemo(() => data?.items ?? [], [data]);
  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);
  const filtered = useMemo(
    () => items.filter((o) => matchesOrderQuery(o, query)),
    [items, query],
  );

  const buckets = useMemo(() => bucketize(filtered), [filtered]);
  const activeCount = buckets.newOrders.length + buckets.preparing.length;

  const newIds = useNewIds(buckets.newOrders.map((o) => o.id));
  useTitleBadge(activeCount);
  useWakeLock(true);

  const accept = useAcceptOrder();
  const complete = useCompleteOrder();
  const searchRef = useRef<HTMLDivElement>(null);

  const handleOpen = (id: number) => {
    setOpenId(id);
    navigate(`/orders/${id}`, { replace: false });
  };

  const handleClose = useCallback(() => {
    setOpenId(null);
    if (paramId) navigate('/orders', { replace: false });
  }, [paramId, navigate]);

  const handleAcceptShortcut = useCallback(() => {
    const target = buckets.newOrders[0];
    if (!target || accept.isPending) return;
    accept.mutate(
      { orderId: target.id },
      {
        onSuccess: () => toast.success(t('orders.toasts.accepted', { id: target.id })),
        onError: (err) => toast.error(t('orders.toasts.actionFailed', { message: err.message })),
      },
    );
  }, [accept, buckets.newOrders, t]);

  const handleCompleteShortcut = useCallback(() => {
    const target = openId
      ? buckets.preparing.find((o) => o.id === openId)
      : buckets.preparing[0];
    if (!target || complete.isPending) return;
    complete.mutate(
      { orderId: target.id },
      {
        onSuccess: () => toast.success(t('orders.toasts.completed', { id: target.id })),
        onError: (err) => toast.error(t('orders.toasts.actionFailed', { message: err.message })),
      },
    );
  }, [complete, buckets.preparing, openId, t]);

  useKeyboardShortcuts({
    a: handleAcceptShortcut,
    c: handleCompleteShortcut,
    escape: () => {
      if (openId != null) handleClose();
    },
    '/': () => {
      const input = searchRef.current?.querySelector('input');
      input?.focus();
      input?.select();
    },
  });

  const totalToday = items.length;
  const subtitle = isLoading
    ? '…'
    : activeCount > 0
      ? `${activeCount} active · ${totalToday} brewed today`
      : `Quiet for the moment · ${totalToday} brewed today`;

  return (
    <div className="flex h-full flex-col">
      <div className="container max-w-screen-2xl shrink-0 space-y-4 px-4 pt-6 lg:px-8">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="label-tracked">Today's rail</div>
            <h1 className="font-mono text-4xl text-foreground">{t('nav.orders')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          </div>
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <div ref={searchRef} className="flex-1 sm:flex-none">
              <SearchField value={query} onChange={setQuery} />
            </div>
            <ShopPauseControl />
          </div>
        </header>
      </div>

      <div className="mt-4 min-h-0 flex-1 overflow-hidden">
        <div className="container max-w-screen-2xl h-full px-0 md:px-4 lg:px-8">
          <div className="scrollbar-hidden flex h-full snap-x snap-mandatory gap-4 overflow-x-auto overflow-y-hidden overscroll-x-contain scroll-px-4 px-4 pb-6 md:grid md:grid-cols-4 md:gap-3 md:overflow-x-visible md:overflow-y-auto md:overscroll-auto md:scroll-px-0 md:px-0">
            <OrderColumn
              tone="new"
              title={t('orders.columns.new')}
              orders={buckets.newOrders}
              newIds={newIds}
              emptyKey="orders.columns.empty.new"
              onOpen={handleOpen}
              onRefresh={handleRefresh}
            />
            <OrderColumn
              tone="preparing"
              title={t('orders.columns.preparing')}
              orders={buckets.preparing}
              newIds={new Set()}
              emptyKey="orders.columns.empty.preparing"
              onOpen={handleOpen}
              onRefresh={handleRefresh}
            />
            <OrderColumn
              tone="completed"
              title={t('orders.columns.completed')}
              orders={buckets.completed}
              newIds={new Set()}
              emptyKey="orders.columns.empty.completed"
              onOpen={handleOpen}
              cap={TERMINAL_CAP}
              showHistoryLink
              onRefresh={handleRefresh}
            />
            <OrderColumn
              tone="other"
              title={t('orders.columns.other')}
              orders={buckets.other}
              newIds={new Set()}
              emptyKey="orders.columns.empty.other"
              onOpen={handleOpen}
              cap={TERMINAL_CAP}
              showHistoryLink
              onRefresh={handleRefresh}
            />
          </div>
        </div>
      </div>

      <OrderDetailDrawer orderId={openId} onClose={handleClose} />

      <OnboardingTour />
    </div>
  );
}

function useNewIds(currentIds: number[]): Set<number> {
  const knownRef = useRef<Set<number>>(new Set());
  const initialisedRef = useRef(false);
  const [newIds, setNewIds] = useState<Set<number>>(new Set());
  const timersRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    const known = knownRef.current;
    if (!initialisedRef.current) {
      currentIds.forEach((id) => known.add(id));
      initialisedRef.current = true;
      return;
    }
    const fresh = currentIds.filter((id) => !known.has(id));
    currentIds.forEach((id) => known.add(id));
    if (fresh.length === 0) return;

    setNewIds((prev) => {
      const next = new Set(prev);
      fresh.forEach((id) => next.add(id));
      return next;
    });

    fresh.forEach((id) => {
      const existing = timersRef.current.get(id);
      if (existing) window.clearTimeout(existing);
      const handle = window.setTimeout(() => {
        setNewIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        timersRef.current.delete(id);
      }, NEW_HIGHLIGHT_MS);
      timersRef.current.set(id, handle);
    });
  }, [currentIds]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((h) => window.clearTimeout(h));
      timers.clear();
    };
  }, []);

  return newIds;
}
